'use client'

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'

interface AccessibilityState {
  fontSize: number          // multiplier, default 1
  textSpacing: boolean
  lineHeight: boolean
  dyslexia: boolean
  adhd: boolean
  saturation: boolean
  darkMode: boolean
  invertColors: boolean
  highlightLinks: boolean
  textToSpeech: boolean
  screenReader: boolean
  bigCursor: boolean
  pauseAnimation: boolean
  hideImages: boolean
}

const defaultState: AccessibilityState = {
  fontSize: 1,
  textSpacing: false,
  lineHeight: false,
  dyslexia: false,
  adhd: false,
  saturation: false,
  darkMode: false,
  invertColors: false,
  highlightLinks: false,
  textToSpeech: false,
  screenReader: false,
  bigCursor: false,
  pauseAnimation: false,
  hideImages: false,
}

interface AccessibilityContextType {
  state: AccessibilityState
  toggle: (key: keyof AccessibilityState) => void
  increaseFontSize: () => void
  decreaseFontSize: () => void
  resetAll: () => void
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}

const AccessibilityContext = createContext<AccessibilityContextType>({
  state: defaultState,
  toggle: () => {},
  increaseFontSize: () => {},
  decreaseFontSize: () => {},
  resetAll: () => {},
  isOpen: false,
  setIsOpen: () => {},
})

export function AccessibilityProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AccessibilityState>(defaultState)
  const [isOpen, setIsOpen] = useState(false)

  // Load from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('pp-accessibility')
      if (saved) {
        setState(JSON.parse(saved))
      }
    } catch {}
  }, [])

  // Apply CSS classes to <html> based on state
  useEffect(() => {
    const html = document.documentElement
    const body = document.body

    // Font size
    html.style.fontSize = `${state.fontSize * 16}px`

    // Text spacing
    body.classList.toggle('acc-text-spacing', state.textSpacing)

    // Line height
    body.classList.toggle('acc-line-height', state.lineHeight)

    // Dyslexia friendly font
    body.classList.toggle('acc-dyslexia', state.dyslexia)

    // ADHD mode (reduce distractions)
    body.classList.toggle('acc-adhd', state.adhd)

    // Saturation
    body.classList.toggle('acc-saturation', state.saturation)

    // Dark mode
    html.classList.toggle('dark', state.darkMode)

    // Invert colors
    body.classList.toggle('acc-invert', state.invertColors)

    // Highlight links
    body.classList.toggle('acc-highlight-links', state.highlightLinks)

    // Big cursor
    body.classList.toggle('acc-big-cursor', state.bigCursor)

    // Pause animations
    body.classList.toggle('acc-pause-animations', state.pauseAnimation)

    // Hide images
    body.classList.toggle('acc-hide-images', state.hideImages)

    // Persist
    localStorage.setItem('pp-accessibility', JSON.stringify(state))
  }, [state])

  // Text-to-speech on any click when enabled
  useEffect(() => {
    if (!state.textToSpeech) return
    const handleClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement
      const text = el.innerText || el.textContent || ''
      if (text.trim() && window.speechSynthesis) {
        window.speechSynthesis.cancel()
        const utterance = new SpeechSynthesisUtterance(text.trim().slice(0, 200))
        window.speechSynthesis.speak(utterance)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [state.textToSpeech])

  // Keyboard shortcut Ctrl+F2
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'F2') {
        e.preventDefault()
        setIsOpen((prev) => !prev)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const toggle = useCallback((key: keyof AccessibilityState) => {
    if (key === 'fontSize') return
    setState((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const increaseFontSize = useCallback(() => {
    setState((prev) => ({ ...prev, fontSize: Math.min(prev.fontSize + 0.1, 1.5) }))
  }, [])

  const decreaseFontSize = useCallback(() => {
    setState((prev) => ({ ...prev, fontSize: Math.max(prev.fontSize - 0.1, 0.7) }))
  }, [])

  const resetAll = useCallback(() => {
    setState(defaultState)
    document.documentElement.style.fontSize = ''
  }, [])

  return (
    <AccessibilityContext.Provider value={{
      state, toggle, increaseFontSize, decreaseFontSize, resetAll, isOpen, setIsOpen,
    }}>
      {children}
    </AccessibilityContext.Provider>
  )
}

export function useAccessibility() {
  return useContext(AccessibilityContext)
}
